/**
 * HelferKontoPage — einheitliches Helfer-Guthaben.
 *
 * Führt die drei bisher getrennten Sichten zusammen: verdient (Stunden × Satz +
 * Pauschalen), belegt (gestellte Rechnungen) und ausgezahlt (alle Auszahlungen),
 * plus die Divergenzen: offen (Guthaben), Rechnung fehlt (bezahlt aber nicht belegt),
 * unberechnet (geleistet aber nicht in Rechnung gestellt).
 */
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MTabs } from "@vimaana/ui";
import { api, type HelferKonto } from "../lib/api";

const TABS = [
  { id: "inbox", label: "Rechnungen", ic: "📥", to: "/inbox" },
  { id: "erfassen", label: "Scannen", ic: "📷", to: "/erfassen" },
  { id: "profil", label: "Profil", ic: "◌", to: "/profil" },
];

function euro(n: number): string {
  return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export default function HelferKontoPage() {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["helfer-konto"], queryFn: () => api.helferKonto(), staleTime: 60 * 1000 });
  const rows = q.data ?? [];

  const sumOffen = rows.reduce((s, r) => s + Math.max(0, r.offen), 0);
  const sumFehlt = rows.reduce((s, r) => s + Math.max(0, r.rechnung_fehlt), 0);

  return (
    <div className="m-app" style={{ minHeight: "100vh", paddingBottom: 90 }}>
      <div style={{ padding: "calc(env(safe-area-inset-top) + 14px) 20px 8px" }}>
        <button
          onClick={() => nav("/profil")}
          style={{ background: "none", border: "none", color: "var(--m-ink-3, #888)", fontSize: 13, padding: 0, marginBottom: 8, cursor: "pointer" }}
        >
          ‹ Profil
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "var(--m-ink, #eee)" }}>Helfer-Konto</h1>
        <p style={{ fontSize: 12.5, color: "var(--m-ink-3, #888)", marginTop: 4, lineHeight: 1.5 }}>
          Verdient (Stunden + Pauschalen) gegen gestellte Rechnungen gegen Auszahlungen.
        </p>
      </div>

      {/* Summen */}
      <div style={{ display: "flex", gap: 10, padding: "0 20px 10px" }}>
        <SumCard label="Offenes Guthaben" wert={euro(sumOffen)} farbe="#e0a53f" hint="verdient − ausgezahlt" />
        <SumCard label="Rechnung fehlt" wert={euro(sumFehlt)} farbe="#e0705a" hint="bezahlt, nicht belegt" />
      </div>

      <div style={{ padding: "0 16px" }}>
        {q.isLoading && <div style={{ padding: 20, color: "var(--m-ink-3, #888)" }}>Lade…</div>}
        {q.isError && <div style={{ padding: 20, color: "tomato" }}>{(q.error as Error).message}</div>}
        {rows.map((r) => (
          <HelferZeile key={r.member_id} r={r} />
        ))}
        {!q.isLoading && rows.length === 0 && (
          <div style={{ padding: 20, color: "var(--m-ink-3, #888)" }}>Keine Helfer mit Bewegungen.</div>
        )}
      </div>

      <MTabs items={TABS} />
    </div>
  );
}

function SumCard({ label, wert, farbe, hint }: { label: string; wert: string; farbe: string; hint: string }) {
  return (
    <div style={{ flex: 1, padding: "11px 13px", borderRadius: 12, background: "var(--m-card, #1a1a1a)", border: "1px solid var(--m-line, #333)" }}>
      <div style={{ fontSize: 11.5, color: "var(--m-ink-3, #888)" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: farbe, marginTop: 2 }}>{wert}</div>
      <div style={{ fontSize: 10.5, color: "var(--m-ink-3, #888)", marginTop: 1 }}>{hint}</div>
    </div>
  );
}

function HelferZeile({ r }: { r: HelferKonto }) {
  const fehlt = r.rechnung_fehlt > 0.005;
  return (
    <div
      style={{
        padding: "12px 14px",
        marginBottom: 8,
        borderRadius: 12,
        background: "var(--m-card, #1a1a1a)",
        border: `1px solid ${fehlt ? "rgba(224,112,90,0.5)" : "var(--m-line, #333)"}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "var(--m-ink, #eee)" }}>
          {r.name}
          {!r.active && <span style={{ fontSize: 11, color: "var(--m-ink-3, #888)", fontWeight: 400 }}> · inaktiv</span>}
        </span>
        <span style={{ fontSize: 15, fontWeight: 800, color: r.offen > 0.005 ? "#e0a53f" : "var(--m-ink-3, #888)" }}>
          {r.offen > 0.005 ? euro(r.offen) : "0,00 €"}
          <span style={{ fontSize: 10.5, fontWeight: 400, color: "var(--m-ink-3, #888)" }}> offen</span>
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 14px", marginTop: 6, fontSize: 11.5, color: "var(--m-ink-3, #999)" }}>
        <span>Verdient <b style={{ color: "var(--m-ink-2, #ccc)" }}>{euro(r.verdient)}</b></span>
        <span>Belegt <b style={{ color: "var(--m-ink-2, #ccc)" }}>{euro(r.belegt)}</b></span>
        <span>Ausgezahlt <b style={{ color: "var(--m-ink-2, #ccc)" }}>{euro(r.ausgezahlt)}</b></span>
      </div>
      {(fehlt || r.unberechnet > 0.005) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 12px", marginTop: 5, fontSize: 11 }}>
          {fehlt && (
            <span style={{ color: "#e0705a", fontWeight: 600 }}>
              ⚠ {euro(r.rechnung_fehlt)} bezahlt ohne Rechnung → Rechnung/Eigenbeleg
            </span>
          )}
          {r.unberechnet > 0.005 && (
            <span style={{ color: "var(--m-ink-3, #888)" }}>{euro(r.unberechnet)} geleistet, noch nicht berechnet</span>
          )}
        </div>
      )}
    </div>
  );
}
